import { useState, useEffect, useRef } from 'react'
import { format } from 'date-fns'
import {
  Car,
  Camera,
  Wrench,
  FileText,
  TrendingUp,
  RefreshCw,
  Gauge,
  Award,
  ChevronDown
} from 'lucide-react'

const VehicleTimeline = ({
  vehicle,
  serviceRecords = [],
  documents = [],
  valuations = [],
  maxItems = 20
}) => {
  const [allEvents, setAllEvents] = useState([])
  const [displayedEvents, setDisplayedEvents] = useState([])
  const [showAll, setShowAll] = useState(false)
  const [visibleCount, setVisibleCount] = useState(maxItems)
  const containerRef = useRef(null)

  // Event type configuration with icon and color
  const eventConfig = {
    vehicle_added: {
      icon: Car,
      color: 'text-rdc-orange bg-orange-50',
      borderColor: 'border-orange-200',
      label: 'Added to garage'
    },
    photo_added: {
      icon: Camera,
      color: 'text-purple-600 bg-purple-50',
      borderColor: 'border-purple-200',
      label: 'Photos uploaded'
    },
    service_record: {
      icon: Wrench,
      color: 'text-blue-600 bg-blue-50',
      borderColor: 'border-blue-200',
      label: 'Service record'
    },
    document_added: {
      icon: FileText,
      color: 'text-green-600 bg-green-50',
      borderColor: 'border-green-200',
      label: 'Document uploaded'
    },
    valuation_added: {
      icon: TrendingUp,
      color: 'text-amber-600 bg-amber-50',
      borderColor: 'border-amber-200',
      label: 'Valuation'
    },
    status_change: {
      icon: RefreshCw,
      color: 'text-gray-600 bg-gray-50',
      borderColor: 'border-gray-200',
      label: 'Status changed'
    },
    mileage_update: {
      icon: Gauge,
      color: 'text-teal-600 bg-teal-50',
      borderColor: 'border-teal-200',
      label: 'Mileage updated'
    },
    milestone: {
      icon: Award,
      color: 'text-rdc-orange bg-orange-50',
      borderColor: 'border-orange-200',
      label: 'Milestone reached'
    }
  }

  // Build all events from various sources
  useEffect(() => {
    const events = []

    // Vehicle added event (from created_at)
    if (vehicle?.created_at) {
      events.push({
        id: 'vehicle-created',
        type: 'vehicle_added',
        date: new Date(vehicle.created_at),
        title: 'Added to garage',
        description: 'Vehicle added to your collection',
        timestamp: new Date(vehicle.created_at).getTime()
      })
    }

    // Service records
    if (serviceRecords && Array.isArray(serviceRecords)) {
      serviceRecords.forEach((record) => {
        events.push({
          id: `service-${record.id}`,
          type: 'service_record',
          date: new Date(record.service_date || record.created_at),
          title: record.service_type || 'Service',
          description: record.description || '',
          cost: record.cost || record.total_cost || null,
          servicedBy: record.serviced_by || null,
          timestamp: new Date(record.service_date || record.created_at).getTime()
        })
      })
    }

    // Documents
    if (documents && Array.isArray(documents)) {
      documents.forEach((doc) => {
        events.push({
          id: `doc-${doc.id}`,
          type: 'document_added',
          date: new Date(doc.uploaded_at || doc.created_at),
          title: doc.document_type ? doc.document_type.replace(/_/g, ' ') : 'Document',
          description: doc.title || doc.file_name || '',
          timestamp: new Date(doc.uploaded_at || doc.created_at).getTime()
        })
      })
    }

    // Valuations
    if (valuations && Array.isArray(valuations)) {
      valuations.forEach((val) => {
        events.push({
          id: `valuation-${val.id}`,
          type: 'valuation_added',
          date: new Date(val.valuation_date || val.created_at),
          title: `Valued at ${formatCurrency(val.estimated_value || val.value)}`,
          description: val.source === 'personal_estimate' ? 'Personal estimate' : val.source || '',
          timestamp: new Date(val.valuation_date || val.created_at).getTime()
        })
      })
    }

    // Sort by date (most recent first)
    events.sort((a, b) => b.timestamp - a.timestamp)
    setAllEvents(events)
    setDisplayedEvents(events.slice(0, visibleCount))
  }, [vehicle, serviceRecords, documents, valuations, visibleCount])

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return '$0'
    return '$' + Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
  }

  // Format date
  const formatDate = (date) => {
    return format(new Date(date), 'MMM d, yyyy')
  }

  // Handle show more
  const handleShowMore = () => {
    setVisibleCount(allEvents.length)
    setShowAll(true)
  }

  // Empty state
  if (allEvents.length === 0) {
    return (
      <div className="card p-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-rdc-cream mb-4">
          <Car size={24} className="text-rdc-warm-gray" />
        </div>
        <h3 className="section-title mb-2">No timeline events yet</h3>
        <p className="text-sm text-rdc-taupe">
          Add your first service record to start building history
        </p>
      </div>
    )
  }

  return (
    <div className="card p-6 md:p-8" ref={containerRef}>
      {/* Header */}
      <h3 className="section-title mb-8 flex items-center gap-2">
        <Car size={20} className="text-rdc-orange" />
        Vehicle Timeline
      </h3>

      {/* Timeline Container */}
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 md:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-rdc-orange via-rdc-warm-gray to-rdc-warm-gray/30" />

        {/* Events */}
        <div className="space-y-6">
          {displayedEvents.map((event, index) => {
            const config = eventConfig[event.type] || eventConfig.service_record
            const IconComponent = config.icon

            return (
              <div
                key={event.id}
                className="relative pl-20 md:pl-24 animate-fade-in"
                style={{
                  animationDelay: `${index * 50}ms`
                }}
              >
                {/* Timeline Node Circle */}
                <div className={`absolute left-0 w-14 md:w-16 h-14 md:h-16 rounded-full flex items-center justify-center border-4 border-rdc-white shadow-sm -ml-7 md:-ml-8 ${config.color}`}>
                  <IconComponent size={24} className="flex-shrink-0" />
                </div>

                {/* Event Content */}
                <div className={`relative border-l-2 ${config.borderColor} pl-4 md:pl-6 py-2`}>
                  {/* Date */}
                  <p className="text-xs font-semibold text-rdc-taupe uppercase tracking-wide mb-1">
                    {formatDate(event.date)}
                  </p>

                  {/* Title */}
                  <h4 className="text-sm md:text-base font-semibold text-rdc-black mb-1">
                    {event.title}
                  </h4>

                  {/* Description and Details */}
                  {(event.description || event.servicedBy || event.cost) && (
                    <div className="text-xs md:text-sm text-rdc-taupe space-y-1">
                      {event.description && (
                        <p>{event.description}</p>
                      )}
                      {event.servicedBy && (
                        <p>Serviced by {event.servicedBy}</p>
                      )}
                      {event.cost && (
                        <p className="font-medium text-rdc-black">
                          Cost: {formatCurrency(event.cost)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Show More Button */}
        {!showAll && allEvents.length > maxItems && (
          <div className="relative pl-20 md:pl-24 mt-8">
            <button
              onClick={handleShowMore}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-rdc-orange bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
            >
              <span>Show {allEvents.length - maxItems} more events</span>
              <ChevronDown size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default VehicleTimeline
